import { getOrderDirectionToSortOrder } from "@/utils/tableUtils";
import { PointsReportOrderByColumn, PointsReportType } from "./enums";
import { PointsReportFilterPageResult } from "./types";

export type PointsReportColumnScope = "TOTAL" | "PERIOD";
export type PointsReportColumnMeasure = "INDIVIDUAL" | "GROUP" | "SUM";

const ORDER_BY_COLUMNS: Record<
  PointsReportColumnScope,
  Record<PointsReportColumnMeasure, Record<"NETTO" | "BRUTTO", PointsReportOrderByColumn>>
> = {
  TOTAL: {
    INDIVIDUAL: {
      NETTO: PointsReportOrderByColumn.TOTAL_INDIVIDUAL_NETTO_POINTS,
      BRUTTO: PointsReportOrderByColumn.TOTAL_INDIVIDUAL_BRUTTO_POINTS,
    },
    GROUP: {
      NETTO: PointsReportOrderByColumn.TOTAL_GROUP_NETTO_POINTS,
      BRUTTO: PointsReportOrderByColumn.TOTAL_GROUP_BRUTTO_POINTS,
    },
    SUM: {
      NETTO: PointsReportOrderByColumn.TOTAL_NETTO_POINTS_SUM,
      BRUTTO: PointsReportOrderByColumn.TOTAL_BRUTTO_POINTS_SUM,
    },
  },
  PERIOD: {
    INDIVIDUAL: {
      NETTO: PointsReportOrderByColumn.PERIOD_INDIVIDUAL_NETTO_POINTS,
      BRUTTO: PointsReportOrderByColumn.PERIOD_INDIVIDUAL_BRUTTO_POINTS,
    },
    GROUP: {
      NETTO: PointsReportOrderByColumn.PERIOD_GROUP_NETTO_POINTS,
      BRUTTO: PointsReportOrderByColumn.PERIOD_GROUP_BRUTTO_POINTS,
    },
    SUM: {
      NETTO: PointsReportOrderByColumn.PERIOD_NETTO_POINTS_SUM,
      BRUTTO: PointsReportOrderByColumn.PERIOD_BRUTTO_POINTS_SUM,
    },
  },
};

const COMMISSION_AMOUNT_ORDER_BY_COLUMNS: Record<PointsReportColumnScope, PointsReportOrderByColumn> = {
  TOTAL: PointsReportOrderByColumn.TOTAL_COMMISSION_AMOUNT,
  PERIOD: PointsReportOrderByColumn.PERIOD_COMMISSION_AMOUNT,
};

/**
 * Columns showing both point types at once are sorted by netto points, which are rendered as their first value.
 * The commission report has a single amount column per period, so the measure does not apply to it.
 */
export const getPointsReportOrderByColumn = (
  scope: PointsReportColumnScope,
  measure: PointsReportColumnMeasure,
  reportType?: PointsReportType,
): PointsReportOrderByColumn =>
  reportType === PointsReportType.COMMISSION_AMOUNTS
    ? COMMISSION_AMOUNT_ORDER_BY_COLUMNS[scope]
    : ORDER_BY_COLUMNS[scope][measure][reportType === PointsReportType.BRUTTO_POINTS ? "BRUTTO" : "NETTO"];

export const getPointsReportSortOrder = (
  orderByColumn: PointsReportOrderByColumn,
  orderByPeriodIndex?: number,
  data?: PointsReportFilterPageResult,
) => {
  const orderByIndex = data?.orderBy.indexOf(orderByColumn) ?? -1;

  if (orderByIndex < 0 || data?.orderByPeriodIndex !== orderByPeriodIndex) {
    return;
  }

  const orderDirection = data?.orderDirections[orderByIndex];

  return orderDirection ? getOrderDirectionToSortOrder(orderDirection) : undefined;
};
